import {
  InvoiceEligibility,
  InvoiceIneligibilityReason,
} from '@/services/api-services/invoice.service';
import { CustomError } from '@/utils';
import { notification } from 'antd';
import { ReactNode } from 'react';
import { getI18n } from 'react-i18next';
import { getProductPageUrl } from './invoicePricingError';

// Kept free of Order form/schema imports so it can be used from
// src/queries/order.query.ts without creating an import cycle.

const INVOICE_ERROR_NOTIFICATION_KEY = 'INVOICE_ERROR';

/**
 * Invoice failures from PUT /orders/:id and
 * POST /orders/:id/regenerate-invoice-documents. Their `errorMessage` is
 * already specific (e.g. "Invoice INV-1234 was not found in Xero."), so it's
 * shown as is rather than a per-code translation.
 */
const INVOICE_ERROR_CODES = [
  'INVOICE_NOT_ELIGIBLE',
  'CREATE_INVOICE_FAILED',
  'XERO_CONTACT_NOT_FOUND',
  'XERO_CREATE_INVOICE_FAILED',
  'XERO_REQUEST_FAILED',
  'INVOICE_PDF_FETCH_FAILED',
  'INVOICE_NOT_FOUND',
  'GENERATE_DELIVERY_ORDER_FAILED',
];

/**
 * Ineligibility reasons that are a fixable setup problem (project, product
 * or Xero contact) - saving would silently skip the invoice, so the save is
 * blocked until it's fixed. The rest (non-SGD project, nothing billable, ...)
 * are orders that are simply never invoiced, and save as before.
 */
export const BLOCKING_INVOICE_INELIGIBILITY_REASONS = [
  InvoiceIneligibilityReason.PROJECT_NOT_FOUND,
  InvoiceIneligibilityReason.PROJECT_NAME_MISSING,
  InvoiceIneligibilityReason.PRODUCT_NOT_FOUND,
  InvoiceIneligibilityReason.PRODUCT_PRICING_NOT_FOUND,
  InvoiceIneligibilityReason.PRODUCT_MISSING_XERO_ACCOUNT_CODE,
  InvoiceIneligibilityReason.XERO_CONTACT_NOT_FOUND,
  InvoiceIneligibilityReason.XERO_CONTACT_NAME_MISMATCH,
];

type InvoiceErrorResponseData = {
  errorCode?: string;
  errorMessage?: string;
  message?: string;
  details?: Record<string, any> | null;
};

const getErrorResponseData = (error: unknown) =>
  (error as CustomError | undefined)?.response?.data as
    | InvoiceErrorResponseData
    | undefined;

export const isInvoiceError = (error: unknown): boolean =>
  INVOICE_ERROR_CODES.includes(getErrorResponseData(error)?.errorCode ?? '');

/**
 * Products to link to from `details` - `{ product: { productId, name } }`
 * (missing pricing) or `{ products: [{ id, name }] }` (missing Xero account
 * code).
 */
const getDetailsProducts = (details?: Record<string, any> | null) => {
  const { products: detailsProducts, product } = details ?? {};
  const products: { id?: string; name?: string }[] = Array.isArray(
    detailsProducts,
  )
    ? detailsProducts
    : product
      ? [{ id: product.productId, name: product.name }]
      : [];

  return products.filter(
    (product): product is { id: string; name?: string } => !!product?.id,
  );
};

const renderDescription = ({
  message,
  lines = [],
  products = [],
}: {
  message?: string | null;
  lines?: string[];
  products?: { id: string; name?: string }[];
}): ReactNode => {
  const i18n = getI18n();
  if (!message && !lines.length && !products.length) return undefined;

  return (
    <>
      {message && <div>{message}</div>}
      {lines.length > 0 && (
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {lines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}
      {products.map(product => (
        <div key={product.id}>
          <a
            href={getProductPageUrl(product.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {product.name
              ? `${i18n.t('product.goToProductPage')}: ${product.name}`
              : i18n.t('product.goToProductPage')}
          </a>
        </div>
      ))}
    </>
  );
};

/**
 * Backend `errorMessage` as the title, plus each of Xero's
 * `details.validationErrors` so the user can fix the product or account code
 * it names.
 */
export const notifyInvoiceError = (error: unknown) => {
  const i18n = getI18n();
  const data = getErrorResponseData(error);
  const errorCode = data?.errorCode ?? '';
  const translatedFallback = i18n.exists(`error.${errorCode}`)
    ? i18n.t(`error.${errorCode}`)
    : i18n.t('failedToCreateInvoice');
  const rawValidationErrors: unknown = data?.details?.validationErrors;
  const validationErrors = Array.isArray(rawValidationErrors)
    ? rawValidationErrors.filter(
        (validationError: unknown): validationError is string =>
          typeof validationError === 'string' && !!validationError,
      )
    : [];

  notification.error({
    key: INVOICE_ERROR_NOTIFICATION_KEY,
    message: data?.errorMessage || data?.message || translatedFallback,
    description: renderDescription({
      lines: validationErrors,
      products: getDetailsProducts(data?.details),
    }),
  });
};

/** check-eligibility said no - shows its readable `message`. */
export const notifyInvoiceNotEligible = (eligibility: InvoiceEligibility) => {
  const i18n = getI18n();

  notification.error({
    key: INVOICE_ERROR_NOTIFICATION_KEY,
    message: i18n.t('order.invoiceNotEligible'),
    description: renderDescription({
      message: eligibility.message,
      products: getDetailsProducts(eligibility.details),
    }),
  });
};
